/* eslint-disable react-refresh/only-export-components */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import type { AuthUser } from "@/types/auth";
import { shouldRefreshAccessToken } from "@/lib/auth/jwtAccess";
import { parseLoginPayload } from "@/lib/auth/parseLoginPayload";
import { loginUser, refreshAccessToken } from "@/services/users.service";

export type AuthContextValue = {
  user: AuthUser | null;
  accessToken: string | null;
  isAuthenticated: boolean;
  login: (email: string, password: string, remember?: boolean) => Promise<void>;
  logout: () => void;
  isLoading: boolean;
};

export const AuthContext = createContext<AuthContextValue | undefined>(
  undefined,
);

const STORAGE_KEY = "formly_auth";

type StoredAuth = {
  user: AuthUser;
  accessToken: string;
  refreshToken?: string;
  remember?: boolean;
};

function readStoredAuth(): {
  parsed: StoredAuth;
  remember: boolean;
} | null {
  const fromLocal = localStorage.getItem(STORAGE_KEY);
  const fromSession = sessionStorage.getItem(STORAGE_KEY);
  const raw = fromLocal ?? fromSession;
  if (!raw) return null;

  const parsed = JSON.parse(raw) as StoredAuth;
  const remember = parsed.remember ?? Boolean(fromLocal);
  return { parsed, remember };
}

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  const tokenRef = useRef<string | null>(null);
  const refreshTokenRef = useRef<string | null>(null);
  const userRef = useRef<AuthUser | null>(null);
  const rememberRef = useRef(false);
  const logoutRef = useRef<() => void>(() => {});
  const refreshInFlightRef = useRef<Promise<string | null> | null>(null);

  const persist = useCallback(
    (
      nextUser: AuthUser | null,
      nextToken: string | null,
      nextRefreshToken: string | null,
      options?: { remember?: boolean },
    ) => {
      const remember = Boolean(options?.remember);
      rememberRef.current = remember;
      const data =
        nextUser && nextToken
          ? JSON.stringify({
              user: nextUser,
              accessToken: nextToken,
              refreshToken: nextRefreshToken,
              remember,
            })
          : null;
      localStorage.removeItem(STORAGE_KEY);
      sessionStorage.removeItem(STORAGE_KEY);
      if (data) {
        (remember ? localStorage : sessionStorage).setItem(STORAGE_KEY, data);
      }
    },
    [],
  );

  const logout = useCallback(() => {
    setUser(null);
    setAccessToken(null);
    userRef.current = null;
    tokenRef.current = null;
    refreshTokenRef.current = null;
    rememberRef.current = false;
    persist(null, null, null);
  }, [persist]);
  logoutRef.current = logout;

  const applyTokens = useCallback(
    (nextAccessToken: string, nextRefreshToken?: string | null) => {
      const resolvedRefreshToken =
        nextRefreshToken ?? refreshTokenRef.current ?? null;
      setAccessToken(nextAccessToken);
      tokenRef.current = nextAccessToken;
      if (resolvedRefreshToken) {
        refreshTokenRef.current = resolvedRefreshToken;
      }
      const currentUser = userRef.current;
      if (currentUser) {
        persist(currentUser, nextAccessToken, resolvedRefreshToken, {
          remember: rememberRef.current,
        });
      }
      return nextAccessToken;
    },
    [persist],
  );

  const tryRefreshAccessToken = useCallback(async (): Promise<string | null> => {
    if (refreshInFlightRef.current) {
      return refreshInFlightRef.current;
    }

    const currentRefreshToken = refreshTokenRef.current;
    if (!currentRefreshToken) {
      return null;
    }

    refreshInFlightRef.current = (async () => {
      try {
        const response = await refreshAccessToken(currentRefreshToken);
        const nextAccessToken = response.accessToken?.trim() || null;
        if (!nextAccessToken) {
          return null;
        }
        applyTokens(
          nextAccessToken,
          response.refreshToken ?? currentRefreshToken,
        );
        return nextAccessToken;
      } catch {
        return null;
      } finally {
        refreshInFlightRef.current = null;
      }
    })();

    return refreshInFlightRef.current;
  }, [applyTokens]);

  useEffect(() => {
    let cancelled = false;

    const bootstrap = async () => {
      try {
        const stored = readStoredAuth();
        if (!stored) {
          return;
        }

        const { parsed, remember } = stored;
        rememberRef.current = remember;
        userRef.current = parsed.user;
        refreshTokenRef.current = parsed.refreshToken ?? null;
        tokenRef.current = parsed.accessToken;

        if (shouldRefreshAccessToken(parsed.accessToken)) {
          const refreshed = await tryRefreshAccessToken();
          if (cancelled) return;
          if (!refreshed) {
            logout();
            return;
          }
          setUser(parsed.user);
          return;
        }

        if (cancelled) return;
        setUser(parsed.user);
        setAccessToken(parsed.accessToken);
      } catch {
        localStorage.removeItem(STORAGE_KEY);
        sessionStorage.removeItem(STORAGE_KEY);
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    };

    void bootstrap();
    return () => {
      cancelled = true;
    };
  }, [logout, tryRefreshAccessToken]);

  const login = useCallback(
    async (email: string, password: string, remember?: boolean) => {
      const resp = await loginUser({ email, password });
      const parsed = parseLoginPayload(resp);

      userRef.current = parsed.user;
      rememberRef.current = Boolean(remember);
      setUser(parsed.user);
      setAccessToken(parsed.accessToken);
      tokenRef.current = parsed.accessToken;
      refreshTokenRef.current = parsed.refreshToken;
      persist(parsed.user, parsed.accessToken, parsed.refreshToken, {
        remember,
      });
    },
    [persist],
  );

  useEffect(() => {
    const originalFetch = window.fetch.bind(window);
    const intercepted: typeof window.fetch = async (input, init) => {
      const requestUrl =
        typeof input === "string"
          ? input
          : input instanceof URL
            ? input.toString()
            : input.url;

      const isAuthCredentialRequest =
        requestUrl.includes("/auth/login") ||
        requestUrl.includes("/auth/refresh-token");

      const headers = new Headers((init && init.headers) || {});
      const token = tokenRef.current;
      if (
        token &&
        !headers.has("Authorization") &&
        !isAuthCredentialRequest
      ) {
        headers.set("Authorization", `Bearer ${token}`);
      }

      let response = await originalFetch(input, { ...(init || {}), headers });

      if (response.status === 401 && !isAuthCredentialRequest) {
        const newToken = await tryRefreshAccessToken();
        if (newToken) {
          const retryHeaders = new Headers((init && init.headers) || {});
          retryHeaders.set("Authorization", `Bearer ${newToken}`);
          response = await originalFetch(input, {
            ...(init || {}),
            headers: retryHeaders,
          });
          if (response.status === 401) {
            logoutRef.current();
          }
        } else {
          logoutRef.current();
        }
      }

      return response;
    };
    window.fetch = intercepted;
    return () => {
      window.fetch = originalFetch;
    };
  }, [tryRefreshAccessToken]);

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      accessToken,
      isAuthenticated: Boolean(user && accessToken),
      login,
      logout,
      isLoading,
    }),
    [user, accessToken, login, logout, isLoading],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
};
